/**
 * Pure color/percentage math for the 360 scan elevation heatmap on the 2D Plot Plan.
 *
 * Shared by:
 *  - src/extensions/labels3dExt.js — computes per-scan ring color from scan.z vs. level bounds
 *  - src/features/Dashboard/components/ElevationLegend.vue — builds the CSS gradient bar
 *
 * No Vuex, no THREE, no side effects. Safe to unit-test in isolation.
 */

const EPSILON = 1e-6;

/**
 * Four-stop heatmap palette. Values are linear RGB in [0, 1].
 * Pure stops — no RGB compromise — so the math is simple and reviewable.
 */
export const HEATMAP_STOPS = Object.freeze([
  Object.freeze({ pct: 0.0,  r: 0.0, g: 0.0, b: 1.0 }), // blue   — floor
  Object.freeze({ pct: 0.33, r: 0.0, g: 1.0, b: 1.0 }), // cyan
  Object.freeze({ pct: 0.66, r: 1.0, g: 1.0, b: 0.0 }), // yellow
  Object.freeze({ pct: 1.0,  r: 1.0, g: 0.0, b: 0.0 }), // red    — ceiling
]);

function clamp01(v) {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

/**
 * Map a scan elevation to a normalized 0..1 percentage within the level.
 *
 * Edge-case behavior:
 *  - Degenerate range (top == bottom) returns 0.5 so the ring color falls on
 *    the cyan→yellow seam (a mid-color) instead of producing NaN.
 *  - Non-finite inputs also return 0.5 — safe default for a "legend centered" look.
 *  - Values outside [bottom, top] are clamped to the spectrum edges.
 *
 * @param {number} z              Scan elevation (world Z)
 * @param {number} bottomElevation Level floor Z
 * @param {number} topElevation    Level ceiling Z
 * @returns {number} percentage in [0, 1]
 */
export function computeElevationPercentage(z, bottomElevation, topElevation) {
  if (!Number.isFinite(z) || !Number.isFinite(bottomElevation) || !Number.isFinite(topElevation)) {
    return 0.5;
  }
  const range = topElevation - bottomElevation;
  if (Math.abs(range) <= EPSILON) {
    return 0.5;
  }
  return clamp01((z - bottomElevation) / range);
}

/**
 * Linearly interpolate a 0..1 percentage across the HEATMAP_STOPS palette.
 * @param {number} pct - percentage in [0, 1]
 * @returns {{ r: number, g: number, b: number }} linear RGB in [0, 1]
 */
export function interpolateHeatColor(pct) {
  const p = clamp01(pct);
  for (let i = 0; i < HEATMAP_STOPS.length - 1; i++) {
    const a = HEATMAP_STOPS[i];
    const b = HEATMAP_STOPS[i + 1];
    if (p >= a.pct && p <= b.pct) {
      const segment = b.pct - a.pct;
      const t = segment <= EPSILON ? 0 : (p - a.pct) / segment;
      return {
        r: lerp(a.r, b.r, t),
        g: lerp(a.g, b.g, t),
        b: lerp(a.b, b.b, t),
      };
    }
  }
  const last = HEATMAP_STOPS[HEATMAP_STOPS.length - 1];
  return { r: last.r, g: last.g, b: last.b };
}

function rgb01ToCss({ r, g, b }) {
  const ri = Math.round(clamp01(r) * 255);
  const gi = Math.round(clamp01(g) * 255);
  const bi = Math.round(clamp01(b) * 255);
  return `rgb(${ri}, ${gi}, ${bi})`;
}

/**
 * Produce a CSS `linear-gradient(...)` string that matches the palette used by the shader,
 * so the legend bar and the rendered scan rings stay visually in sync.
 *
 * @param {string} [direction='to top'] - CSS gradient direction.
 * @returns {string} linear-gradient string usable as `background:` value.
 */
export function cssGradientString(direction = 'to top') {
  const stops = HEATMAP_STOPS
    .map((stop) => `${rgb01ToCss(stop)} ${(stop.pct * 100).toFixed(0)}%`)
    .join(', ');
  return `linear-gradient(${direction}, ${stops})`;
}
